import React from 'react';
import { Button, Drawer, Menu, Avatar, type MenuProps } from 'antd';
import { palette, typography, dimensions } from '../theme';
import { useResponsiveSizes } from '../../hooks/use-responsive-sizes';
import type { PlatformModule } from './platform-header';

const { base, grey, purple } = palette.primitives;
const { fontFamily, fontWeight } = typography;
const { radius } = dimensions;

const bg = base.white;
const textPrimary = grey.grey800;
const textMuted = grey.grey500;
const borderColor = grey.grey200;
const accent = purple.purple500;
const activeBg = purple.purple0;

interface MobileDrawerProps {
  open: boolean;
  onClose: () => void;
  userName?: string;
  userEmail?: string;
  menuItems?: MenuProps['items'];
  activeKey?: string;
  onMenuSelect?: (key: string) => void;
  modules: PlatformModule[];
  activeModule: string;
  onModuleChange: (id: string) => void;
  onSignOut: () => void;
  extraContent?: React.ReactNode;
}

export const MobileDrawer: React.FC<MobileDrawerProps> = ({
  open,
  onClose,
  userName,
  userEmail,
  menuItems,
  activeKey,
  onMenuSelect,
  modules,
  activeModule,
  onModuleChange,
  onSignOut,
  extraContent
}) => {
  const { fontSize, spacing } = useResponsiveSizes();

  const sectionPadding = `${spacing.md}px ${spacing.lg}px`;
  const sectionLabelStyle: React.CSSProperties = {
    color: textMuted,
    fontFamily: fontFamily.ui,
    fontSize: fontSize.xs,
    fontWeight: fontWeight.medium,
    letterSpacing: '1px',
    marginBottom: spacing.sm,
    textTransform: 'uppercase'
  };

  return (
    <Drawer
      placement='right'
      open={open}
      onClose={onClose}
      width={280}
      closable={false}
      styles={{
        body: { padding: 0, background: bg, display: 'flex', flexDirection: 'column', height: '100%' },
        header: { display: 'none' }
      }}
    >
      <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
        {menuItems && menuItems.length > 0 && (
          <Menu
            mode='vertical'
            selectedKeys={activeKey ? [activeKey] : []}
            onClick={({ key }) => {
              onMenuSelect?.(key);
              onClose();
            }}
            items={menuItems}
            theme='light'
            style={{
              background: bg,
              border: 'none',
              fontFamily: fontFamily.ui,
              fontSize: fontSize.xlg
            }}
          />
        )}

        {modules.length > 1 && (
          <div style={{ padding: sectionPadding }}>
            <div style={sectionLabelStyle}>Módulos</div>
            {modules.map(m => {
              const isActive = m.id === activeModule;
              return (
                <Button
                  key={m.id}
                  type='text'
                  onClick={() => {
                    onModuleChange(m.id);
                    onClose();
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: spacing.sm,
                    width: '100%',
                    justifyContent: 'flex-start',
                    padding: `${spacing.sm}px ${spacing.md}px`,
                    height: 'auto',
                    color: isActive ? accent : textPrimary,
                    fontFamily: fontFamily.ui,
                    fontWeight: isActive ? fontWeight.semibold : fontWeight.regular,
                    fontSize: fontSize.base,
                    background: isActive ? activeBg : 'transparent',
                    borderRadius: radius.lg,
                    marginBottom: spacing.xs
                  }}
                >
                  <span style={{ display: 'flex', color: isActive ? accent : textMuted }}>{m.icon}</span>
                  {m.label}
                </Button>
              );
            })}
          </div>
        )}

        {extraContent && (
          <div style={{ padding: sectionPadding, borderTop: `1px solid ${borderColor}` }}>
            <div style={sectionLabelStyle}>Carpetas</div>
            {extraContent}
          </div>
        )}
      </div>

      <div style={{ padding: sectionPadding, borderTop: `1px solid ${borderColor}` }}>
        <Button
          onClick={() => {
            onSignOut();
            onClose();
          }}
          style={{
            fontFamily: fontFamily.ui,
            fontSize: fontSize.base,
            fontWeight: fontWeight.medium,
            background: bg,
            color: textPrimary,
            border: `1px solid ${borderColor}`,
            width: '100%'
          }}
        >
          Salir
        </Button>
      </div>
    </Drawer>
  );
};
